import type { TranslateFn } from '@eliadmin/core/src/hooks/use-translations';
import { enUS, cs, de, fr, es, ru, ro, sk, hu, pl, it, sl, hr } from 'date-fns/locale';
import { format, formatDistanceToNow, type Locale } from 'date-fns';
import type { Customer } from '@eliadmin/core/src/api/customers/customers-types';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';

export const yesNoOptions = (t: TranslateFn) => [
  { value: 'y', label: t('text1550') },
  { value: 'n', label: t('text1551') },
];

// Map the language codes to date-fns locales
const locales: { [key: string]: Locale } = {
  sk: sk, // Slovak
  cs: cs, // Czech
  en: enUS, // English
  de: de, // German
  hu: hu, // Hungarian
  pl: pl, // Polish
  it: it, // Italian
  si: sl, // Slovenian ('si' is the country code, 'sl' is the language code)
  hr: hr, // Croatian
  fr: fr, // French
  ru: ru, // Russian
  ro: ro, // Romanian
  es: es, // Spanish
};

// Get the locale based on the language code
export const getLocaleForDateFns = (language: string) => locales[language] || enUS;

export const createFittingColor = (hexColor: string | null, magnitude: number): string | null => {
  if (!hexColor || !magnitude) {
    return null;
  }
  // Remove the '#' if it's present
  hexColor = hexColor.replace('#', '');

  if (hexColor.length === 6 || hexColor.length === 8) {
    const hasAlpha = hexColor.length === 8;

    const decimalColor = parseInt(hexColor.substring(0, 6), 16); // Parse the RGB part of the hex

    let r = (decimalColor >> 16) + magnitude;
    r = Math.min(255, Math.max(0, r)); // Ensure r is between 0 and 255

    let g = ((decimalColor >> 8) & 0x00ff) + magnitude;
    g = Math.min(255, Math.max(0, g)); // Ensure g is between 0 and 255

    let b = (decimalColor & 0x0000ff) + magnitude;
    b = Math.min(255, Math.max(0, b)); // Ensure b is between 0 and 255

    const newHex = ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0'); // Convert to hex and pad

    if (hasAlpha) {
      const alpha = hexColor.substring(6, 8); // Extract the alpha part (last 2 characters)
      return `#${newHex}${alpha}`; // Return the new color with alpha included
    } else {
      return `#${newHex}`; // Return the new color without alpha
    }
  } else {
    return `#${hexColor}`; // Return the original hex if it's not a valid 6/8-length hex string
  }
};

export const formatTime = (dateString: string, locale: string) => {
  return formatDistanceToNow(new Date(dateString), {
    addSuffix: true,
    locale: getLocaleForDateFns(locale),
  });
};

export const formatNumber = (
  input: number | string,
  decimalPlaces: number = 0,
  decimalSeparator: string = '.',
  groupSeparator: string = ' ',
): string => {
  // Convert input to a number if it's a string
  const num = typeof input === 'string' ? parseFloat(input) : input;

  // Handle cases where the input is invalid (NaN)
  if (isNaN(num)) {
    return '';
  }

  // Format the number with the specified number of decimal places
  const parts = num.toFixed(decimalPlaces).split('.');

  // Add grouping separator to the integer part
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);

  // Join integer and decimal parts with the specified decimal separator
  return parts.join(decimalSeparator);
};

export const WORKING_HOURS_SPECIFIC_TIME_ID = 3;

export const checkIfProfileCompleted = (customer: Customer, t: TranslateFn) => {
  let completed = true;
  const uncompletedFields = [];

  const fields = [
    { key: 'name', label: t('text33') },
    { key: 'surname', label: t('text34') },
    { key: 'date_of_birth', label: t('text389') },
    { key: 'gender', label: t('text255') },
    { key: 'country', label: t('text41') },
    { key: 'city', label: t('text39') },
    // { key: 'distance_to_work_kilometers', label: t('text86') },
    { key: 'disciplines', label: t('text1599') },
    { key: 'categories', label: t('text1600') },
    { key: 'expected_salary', label: t('text1601') },
    { key: 'employment_types', label: t('text894') },
    { key: 'working_hours', label: t('text1603') },
    { key: 'values', label: t('text2064') },
    { key: 'school', label: t('text885') },
    { key: 'phone', label: t('text36') }, // Assuming 'phone' is a required field but missing from the form
  ];

  if (!customer.start_date && !customer.starting_date_immediately) {
    uncompletedFields.push(t('text923')); // Start date label
  }

  fields.forEach(({ key, label }) => {
    const value = customer[key as keyof Customer];

    if (
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0)
    ) {
      uncompletedFields.push(label);
    }
  });

  if (uncompletedFields.length > 0) {
    completed = false;
  }

  return {
    completed: completed,
    message: `${t('text2062')}\n ${uncompletedFields.join('\n')}`,
    uncompletedFields: uncompletedFields,
  };
};

export const formatAddress = (company: any): string => {
  if (!company) return '';

  // Build the first part: address + street number.
  const addressLine = [company.address, company.street_number].filter(Boolean).join(' ');

  // Build the second part: postcode + city.
  const cityLine = [company.postcode, company.city].filter(Boolean).join(' ');

  // Join both parts with a comma if both exist.
  return [addressLine, cityLine].filter(Boolean).join(', ');
};

export const isPartner = (data: any): { expires: string | null; name: string } | null => {
  if (data?.active_subscriptions?.length) {
    return {
      expires: data?.active_subscriptions?.[0]?.expires_at
        ? format(data?.active_subscriptions?.[0]?.expires_at, 'dd.MM.yyyy')
        : null,
      name: data?.active_subscriptions?.[0]?.name,
    };
  }
  return null;
};

export const formatUrl = (url: string, show = false): string => {
  if (!url) return '';

  if (show) {
    // Remove protocol and prepend "www.", but keep the path (and optionally query/hash)
    try {
      const { hostname, pathname, search, hash } = new URL(
        url?.startsWith('http') ? url : `https://${url}`,
      );
      return `www.${hostname.replace(/^www\./, '')}${pathname}${search}${hash}`;
    } catch (e) {
      return url; // fallback if URL is invalid
    }
  } else {
    // Ensure it starts with http/https
    return url?.startsWith('http') ? url : `https://${url}`;
  }
};
